// -------------------------------
// EXPENSE CATEGORY INTERFACES
// -------------------------------
export interface PendingExpenseCategory {
    category?: string;
    subCategory?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteExpenseCategory {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE EXPENSE CATEGORY — START
// -------------------------------
export const startCreateExpenseCategoryFlow = (
    setPending: (data: PendingExpenseCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    categoryFromIntent?: string
) => {
    if (categoryFromIntent) {
        setPending({ category: categoryFromIntent });

        pushAIChat(`Got it! What is the sub category for "${categoryFromIntent}"?`);
        speak(`What is the sub category for ${categoryFromIntent}?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the expense category be called?");
    speak("What should the expense category be called?");
};

// -------------------------------
// CREATE EXPENSE CATEGORY — PROCESS
// -------------------------------
export const processPendingExpenseCategory = (
    text: string,
    pending: PendingExpenseCategory,
    setPending: (data: PendingExpenseCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — CATEGORY NAME
    if (!pending.category) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid category name.");
            speak("Please provide a valid category name.");
            return;
        }

        setPending({ ...pending, category: cleanName });

        pushAIChat(`What is the sub category for "${cleanName}"?`);
        speak(`What is the sub category for ${cleanName}?`);
        return;
    }

    // STEP 2 — SUB CATEGORY
    if (!pending.subCategory) {
        const subCategory = text.trim();

        if (!subCategory) {
            pushAIChat("Please provide a valid sub category.");
            speak("Please provide a valid sub category.");
            return;
        }

        setPending({ ...pending, subCategory });

        pushAIChat("Should this category be active or inactive?");
        speak("Should this category be active or inactive?");
        return;
    }

    // STEP 3 — STATUS (FINAL)
    if (!pending.status) {
        const status =
            text.toLowerCase().includes("inactive") ? "inactive" : "active";

        setPending({ ...pending, status });

        pushAIChat(`Creating expense category "${pending.category}"...`);
        speak(`Creating expense category ${pending.category}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-expense-category", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPending(null);
        return;
    }
};

// -------------------------------
// DELETE EXPENSE CATEGORY — START
// -------------------------------
export const startDeleteExpenseCategoryFlow = (
    setPendingDelete: (data: PendingDeleteExpenseCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting expense category "${nameFromIntent}"...`);
        speak(`Deleting expense category ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-expense-category", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which expense category would you like to delete?");
    speak("Which expense category would you like to delete?");
};

// -------------------------------
// DELETE EXPENSE CATEGORY — PROCESS
// -------------------------------
export const processPendingDeleteExpenseCategory = (
    text: string,
    pending: PendingDeleteExpenseCategory,
    setPendingDelete: (data: PendingDeleteExpenseCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid category name.");
        speak("Please provide a valid category name.");
        return;
    }

    pushAIChat(`Deleting expense category "${cleanName}"...`);
    speak(`Deleting expense category ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-expense-category", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};
